import PropTypes from 'prop-types'
import React, { useEffect, useState, Fragment } from 'react'
import { Col } from 'react-bootstrap'
import { connect } from 'react-redux'

import { FAQ, InlineLoader, TrialLayout } from '../../components'
import {
  selectCurrentPhoneId,
  selectCurrentPhone,
  selectPreSaleFlag,
  selectRoutingKey,
  selectLanguage
} from '../App/selectors'
import {
  selectCurrentPhoneStarterContentId,
  selectStarterContentFetching
} from '../GettingStarted/selectors'
import {
  selectQuestions,
  selectFaqFetching,
  selectActiveFAQ
} from './selectors'
import { fetchFAQ, setActiveFAQ } from './actions'
import { createEvent } from '../UserEvents/actions'
import contentTypes from '../../constants/content-types'
import LanguageProvider from '../LanguageProvider/LanguageProvider'
import { DeviceNotLoadedHOC } from '../../hocs'
import { ASSETS_URL } from '../../constants/urls'
import SEO from '../../components/SEO'
import {
  faqStructuredData,
  trimString,
  faqsFlat,
  faqsExtractData
} from '../../helpers/utils'

const FaqsContainer = ({
  toggleDeviceModal,
  categories,
  params,
  language,
  createUserEvent,
  currentPhone,
  preSaleFlag,
  modalOpen,
  setActiveFaq,
  activeFAQ,
  query,
  isFetching,
  isFetchingDefaultFAQs,
  defaultFAQId,
  router
}) => {
  /** faq-specific state variables */
  const [faqIdOrSlug, setFaqIdOrSlug] = useState()
  // const [flattenedFaqs, setFlattenedFaqs] = useState([])

  /** device-specific state variables */
  const [imageSrc, setImageSrc] = useState('')
  const [isSameFaq, setIsSameFaq] = useState(false);
  const [imageAlt, setImageAlt] = useState('')

  /** per-faq page-specific state variables */
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [structuredHeadData, setStructuredHeadData] = useState('')

  /**
   * Set `faqIdOrSlug` state variable to the incoming`:faqId` param
   */
  useEffect(() => {
    setFaqIdOrSlug(params.faqId)
  }, [params.faqId])

  /**
   * Set the `activeFAQ` to the record associated with `faqIdOrSlug`
   */
  useEffect(() => {
    if (!faqIdOrSlug || categories.length === 0) {
      return
    }
    /**
     * Flatten the `categories` array by traversing `categories[x].children[x].children`
     * To an array in the shape of {@see FaqsContainer.propTypes.categories}
     */
    const flattenedFaqs = faqsFlat(categories)
    const faqBySlug = flattenedFaqs.find(faq => faq.slug === faqIdOrSlug)
    if (faqBySlug) {
      setActiveFaq({
        faqId: faqBySlug.pk,
        faqName: faqBySlug.name,
        faqSlug: faqBySlug.slug,
        faqVal: faqBySlug.val
      })
      return
    }

    /**
     *  Always replace the `:faqId` with the record's `slug` equivalent
     * ie: `1646271` -> `why-cant-i-find-my-locaion-in-google-maps`
     */
    const faqById = flattenedFaqs.find(
      faq => parseInt(faq.pk, 10) === parseInt(faqIdOrSlug, 10)
    )
    if (faqById) {
      router.replace({
        pathname: `/faqs/${faqById.slug}`,
        query
      })

      return
    }

    /** Otherwise, send to faqs home */
    router.push({
      pathname: `/faqs`,
      query
    })
  }, [faqIdOrSlug, categories])

  /**
   * Set site-wide side-effects for per-faq dynamic variables
   */
  useEffect(() => {
    if (activeFAQ.faqId && currentPhone) {
      setDescription(trimString(activeFAQ.faqVal, 120))
      setStructuredHeadData(
        faqStructuredData([
          { question: activeFAQ.faqName, answer: activeFAQ.faqVal }
        ])
      )
    } else {
      setDescription(
        `Get more information on your ${
          currentPhone ? currentPhone.name_pretty : `device`
        } by browsing knowledge base for FAQs.`
      )
      setStructuredHeadData(
        faqStructuredData([...faqsExtractData(faqsFlat(categories))])
      )
    }
  }, [activeFAQ.faqId, currentPhone])

  /**
   * Set page-specific side-effects for per-selected-device dynamic variables
   */
  useEffect(() => {
    if (currentPhone) {
      setImageSrc(`${ASSETS_URL}${currentPhone.image_location}`)
      setImageAlt(`${currentPhone.manufacturer} ${currentPhone.name}`)
    }
  }, [currentPhone])

  /**
   * Function to set the `activeFAQ` to the record associated with an `faqId` param
   * and push the `browserHistory` to a new route with its `slug` value in the endpoint
   */
  const toggleFaq = faqId => () => {
    setIsSameFaq(activeFAQ.faqId === faqId && !isSameFaq);
    if (activeFAQ.faqId === faqId) return
    const faq = faqsFlat(categories).find(
      faq => parseInt(faq.pk, 10) === parseInt(faqId, 10)
    )
    setActiveFaq({
      faqId: faq.pk,
      faqName: faq.name,
      faqSlug: faq.slug,
      faqVal: faq.val
    })

    createUserEvent({
      category: 'faqs',
      data: { action: 'ToggleSingleFAQ', target: faq.pk }
    })

    router.push({
      pathname: `/faqs/${faq.slug}`,
      query
    })
  }

  const loaderStyles = {
    left: '50%',
    position: 'absolute',
    transform: 'translate(-50%)',
    top: '80px'
  }

  if (params.faqId && !activeFAQ.faqId) {
    return null
  }

  return (
    <Fragment>
      <SEO
        title={`${title} | ${currentPhone ? currentPhone.name_pretty : ''}`}
        image={imageSrc}
        description={description}
        script={structuredHeadData}
      />
      <div id="content">
        <TrialLayout
          createUserEvent={createUserEvent}
          isFetching={isFetchingDefaultFAQs}
          noSideBar={false}
          noSearchBar={preSaleFlag}
          relatedType="related"
          contentType="faq"
          relatedId={
            parseInt(activeFAQ.faqId, 10) || parseInt(defaultFAQId, 10)
          }
          title={contentTypes[language].faqLong}
        >
          <Col
            xsHidden
            smHidden
            md={3}
            className="sidebar"
            style={{ borderRight: '0' }}
          >
            <img width={120} alt={imageAlt} src={imageSrc} />
          </Col>
          <Col xs={12} md={9} className="col">
            {isFetching ? (
              <InlineLoader style={loaderStyles} />
            ) : (
              <FAQ
               isSameFaq={isSameFaq}
                categories={categories}
                toggleFaq={toggleFaq}
                activeFaqId={activeFAQ.faqId}
                createUserEvent={createUserEvent}
                modelOpen={modalOpen}
              />
            )}
          </Col>
        </TrialLayout>
      </div>
    </Fragment>
  )
}

FaqsContainer.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      accadLink: PropTypes.string,
      name: PropTypes.string,
      icon: PropTypes.string,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          children: PropTypes.arrayOf(
            PropTypes.shape({
              academyCategoryFk: PropTypes.number,
              name: PropTypes.string,
              outputOrder: PropTypes.number,
              phoneFk: PropTypes.number,
              pk: PropTypes.number,
              presales: PropTypes.number,
              slug: PropTypes.string,
              val: PropTypes.string
            })
          ),
          name: PropTypes.string
        })
      )
    })
  ).isRequired,
  currentPhone: PropTypes.shape({
    customerID: PropTypes.string,
    id: PropTypes.number
  }),
  currentPhoneId: PropTypes.number.isRequired,
  deviceInvalid: PropTypes.func.isRequired,
  params: PropTypes.shape({ faqId: PropTypes.string }).isRequired,
  language: PropTypes.string.isRequired,
  createUserEvent: PropTypes.func.isRequired,
  preSaleFlag: PropTypes.bool.isRequired,
  modalOpen: PropTypes.bool.isRequired,
  setActiveFaq: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  activeFAQ: PropTypes.shape({
    faqId: PropTypes.number,
    faqName: PropTypes.string,
    faqSlug: PropTypes.string,
    faqVal: PropTypes.string
  }).isRequired,
  query: PropTypes.object.isRequired,
  toggleDeviceModal: PropTypes.func.isRequired,
  isFetchingDefaultFAQs: PropTypes.bool.isRequired,
  defaultFAQId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

const mapStateToProps = (state, ownProps) => ({
  key: selectRoutingKey(state),
  currentPhoneId: selectCurrentPhoneId(state),
  currentPhone: selectCurrentPhone(state),
  preSaleFlag: selectPreSaleFlag(state),
  categories: selectQuestions(state),
  language: selectLanguage(state),
  isFetching: selectFaqFetching(state),
  activeFAQ: selectActiveFAQ(state),
  defaultFAQId: selectCurrentPhoneStarterContentId(state),
  isFetchingDefaultFAQs: selectStarterContentFetching(state),
  query: state.routing.locationBeforeTransitions.query,
  modalOpen: ownProps.modalOpen
})

const mapDispatchToProps = dispatch => ({
  fetchFaq: currentPhoneId => dispatch(fetchFAQ.request(currentPhoneId)),
  setActiveFaq: activeFAQ => dispatch(setActiveFAQ(activeFAQ)),
  createUserEvent: event => dispatch(createEvent(event))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LanguageProvider(DeviceNotLoadedHOC(FaqsContainer)))



// WEBPACK FOOTER //
// ./src/containers/FAQs/FAQs.js